import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell, faTimes, faEye, faProjectDiagram, faFilePdf, faDownload,
  faClipboardList, faBan, faTrash, faCheckCircle, faExclamationTriangle,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import './PaymentRemindersModal.css';

// Statuses at which an issued invoice is "live" and can be voided (mirrors the
// list screen's gate so the reused Actions dropdown offers the same options)
const VOIDABLE_STATUSES = ['APPROVED', 'SENT', 'PAID', 'OVERDUE'];

export const BUCKETS = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_soon', label: 'Due in 5 days' },
];

export const NO_CONTACT_REASON = 'No contact email in CRM';

/**
 * Can this row be ticked?
 *
 * A row whose partner has no mailable CRM contact arrives with an empty
 * recipient and is disabled with a visible reason — never silently skipped.
 * Because the address is editable, typing one in makes the row sendable again;
 * an empty field is the only thing that blocks it.
 */
export const isRowSendable = (recipientValue) => Boolean((recipientValue || '').trim());

const formatAmount = (amount) => {
  const value = parseFloat(amount);
  if (isNaN(value)) return '0.00';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : 'N/A');

/**
 * Payment Reminders lightbox for the Outgoing Invoices screen.
 *
 * Lists the unpaid invoices in one of two buckets (Overdue / Due in 5 days),
 * prefills each row's recipient from the partner's CRM contact, and sends the
 * templated reminder for the ticked rows. Which of the two texts goes out is
 * decided by the backend from the invoice due date.
 *
 * Props:
 *  - isOpen: bool
 *  - onClose(): dismiss
 *  - refreshToken: change it to force a refetch (e.g. after a void/delete)
 *  - isLoadingPreview: bool, mirrors the list screen's PDF preview state
 *  - actions: the list screen's own handlers, reused verbatim so the dropdown
 *    behaves identically here — { onView, onViewRelationships, onPreviewPdf,
 *    onDownloadPdf, onViewAuditLog, onVoid, onDelete }. Edit is deliberately
 *    absent from this dropdown.
 *  - showActions: false drops the Actions column (the CRM entry for credit
 *    approvers, who have no Financials module to act in).
 */
const PaymentRemindersModal = ({
  isOpen,
  onClose,
  refreshToken,
  isLoadingPreview = false,
  actions = {},
  // Opened from the CRM by a credit approver (no Financials module), the
  // invoice actions would lead to screens they cannot open: leave them out.
  showActions = true,
}) => {
  const [bucket, setBucket] = useState('overdue');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [recipients, setRecipients] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [results, setResults] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await invoicingService.fetchPaymentReminders(bucket);
      const fetched = data.results || [];
      setRows(fetched);
      setRecipients(
        fetched.reduce((acc, row) => {
          acc[row.id] = row.suggested_recipient_email || '';
          return acc;
        }, {})
      );
      setSelectedIds([]);
      setResults({});
    } catch (error) {
      console.error('Error loading payment reminders:', error);
      setRows([]);
      setLoadError(
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to load invoices for reminders. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [bucket]);

  useEffect(() => {
    if (isOpen) loadRows();
  }, [isOpen, loadRows, refreshToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const sendableIds = rows
    .filter((row) => isRowSendable(recipients[row.id]))
    .map((row) => row.id);
  const allSendableSelected =
    sendableIds.length > 0 && sendableIds.every((id) => selectedIds.includes(id));

  const toggleRow = (id) => {
    setConfirmArmed(false);
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((rowId) => rowId !== id) : [...current, id]
    );
  };

  const toggleAll = () => {
    setConfirmArmed(false);
    setSelectedIds(allSendableSelected ? [] : sendableIds);
  };

  const setRecipient = (id, value) => {
    setConfirmArmed(false);
    setRecipients((current) => ({ ...current, [id]: value }));
    // A recipient that no longer qualifies must not stay ticked
    if (!isRowSendable(value)) {
      setSelectedIds((current) => current.filter((rowId) => rowId !== id));
    }
  };

  const handleSend = async () => {
    if (!selectedIds.length || isSending) return;

    // Two-step confirm in the modal itself (same guard as VoidInvoiceModal):
    // first click arms the button, second click sends. Any change to the
    // selection or a recipient disarms it.
    if (!confirmArmed) {
      setConfirmArmed(true);
      return;
    }
    setConfirmArmed(false);

    setIsSending(true);
    try {
      const payload = selectedIds.map((id) => ({
        id,
        recipient_email: (recipients[id] || '').trim(),
      }));
      const response = await invoicingService.sendPaymentReminders(payload);
      const byId = (response.results || []).reduce((acc, row) => {
        acc[row.id] = row;
        return acc;
      }, {});
      setResults((current) => ({ ...current, ...byId }));
      // Anything that went out is unticked so a second click can't resend it
      setSelectedIds((current) => current.filter((id) => byId[id]?.status !== 'sent'));
    } catch (error) {
      console.error('Error sending payment reminders:', error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to send reminders. Please try again.';
      setResults((current) => ({
        ...current,
        ...selectedIds.reduce((acc, id) => {
          acc[id] = { id, status: 'failed', error: message };
          return acc;
        }, {}),
      }));
    } finally {
      setIsSending(false);
    }
  };

  const runAction = (handler) => (event) => {
    setActiveDropdown(null);
    if (typeof handler === 'function') handler(event);
  };

  const renderRowStatus = (row) => {
    const result = results[row.id];
    if (!result) return null;
    if (result.status === 'sent') {
      return (
        <span className="payment-reminders-row-status is-sent">
          <FontAwesomeIcon icon={faCheckCircle} /> Sent
        </span>
      );
    }
    return (
      <span className="payment-reminders-row-status is-failed" title={result.error || ''}>
        <FontAwesomeIcon icon={faExclamationTriangle} /> Failed — {result.error}
      </span>
    );
  };

  return (
    <div className="payment-reminders-overlay" onClick={() => !isSending && onClose()}>
      <div className="payment-reminders-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-reminders-header">
          <div className="payment-reminders-header-title">
            <FontAwesomeIcon icon={faBell} />
            <h3>Payment Reminders</h3>
          </div>
          <button
            type="button"
            className="payment-reminders-close"
            onClick={onClose}
            disabled={isSending}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="payment-reminders-buckets">
          {BUCKETS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`payment-reminders-bucket ${bucket === option.key ? 'is-active' : ''}`}
              onClick={() => setBucket(option.key)}
              disabled={isSending}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="payment-reminders-body">
          {loading ? (
            <div className="payment-reminders-empty">Loading invoices…</div>
          ) : loadError ? (
            <div className="payment-reminders-error">{loadError}</div>
          ) : rows.length === 0 ? (
            <div className="payment-reminders-empty">
              {bucket === 'overdue'
                ? 'No overdue invoices to chase.'
                : 'No invoices falling due in the next 5 days.'}
            </div>
          ) : (
            <table className="payment-reminders-table">
              <thead>
                <tr>
                  <th>Invoice Number</th>
                  <th>IMT Entity</th>
                  <th>Partner Name</th>
                  <th>Invoice Date</th>
                  <th>Due Date</th>
                  <th className="is-amount">Gross Amount</th>
                  <th className="is-send">
                    <label className="payment-reminders-tick">
                      <input
                        type="checkbox"
                        checked={allSendableSelected}
                        onChange={toggleAll}
                        disabled={isSending || sendableIds.length === 0}
                        aria-label="Select all invoices with a recipient"
                      />
                      <span>Send Reminder</span>
                    </label>
                  </th>
                  {showActions && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const recipient = recipients[row.id] || '';
                  const sendable = isRowSendable(recipient);
                  return (
                    <tr key={row.id} className={sendable ? '' : 'is-blocked'}>
                      <td className="payment-reminders-number">{row.invoice_number || 'N/A'}</td>
                      <td>{row.hms_entity_name || 'N/A'}</td>
                      <td>{row.partner_name || 'N/A'}</td>
                      <td>{formatDate(row.invoice_date)}</td>
                      <td>{formatDate(row.due_date)}</td>
                      <td className="is-amount">
                        {row.currency || 'USD'} {formatAmount(row.gross_amount)}
                      </td>
                      {/* Send Reminder — the tickbox and the address it would
                          go to live in one cell, so the header row stays the
                          exact column set the task card specifies. */}
                      <td className="is-send">
                        <div className="payment-reminders-send-cell">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row.id)}
                            onChange={() => toggleRow(row.id)}
                            disabled={!sendable || isSending}
                            title={sendable ? '' : NO_CONTACT_REASON}
                            aria-label={`Send reminder for invoice ${row.invoice_number}`}
                          />
                          <input
                            type="text"
                            className="payment-reminders-recipient-input"
                            value={recipient}
                            placeholder="name@company.com"
                            onChange={(e) => setRecipient(row.id, e.target.value)}
                            disabled={isSending}
                            aria-label={`Recipient email for invoice ${row.invoice_number}`}
                          />
                        </div>
                        {row.recipient_source === 'none' && (
                          <span className="payment-reminders-reason">
                            <FontAwesomeIcon icon={faExclamationTriangle} /> {NO_CONTACT_REASON}
                          </span>
                        )}
                        {renderRowStatus(row)}
                      </td>
                      {showActions && (
                      <td className="payment-reminders-actions">
                        <div
                          className="payment-reminders-dropdown"
                          ref={activeDropdown === row.id ? dropdownRef : null}
                        >
                          <button
                            type="button"
                            className="payment-reminders-actions-button"
                            onClick={() =>
                              setActiveDropdown(activeDropdown === row.id ? null : row.id)
                            }
                          >
                            Actions
                          </button>
                          {activeDropdown === row.id && (
                            <div className="payment-reminders-dropdown-menu">
                              <button type="button" onClick={runAction((e) => actions.onView?.(row, e))}>
                                <FontAwesomeIcon icon={faEye} /> View
                              </button>
                              <button type="button" onClick={runAction((e) => actions.onViewRelationships?.(row, e))}>
                                <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                              </button>
                              <button
                                type="button"
                                onClick={runAction((e) => actions.onPreviewPdf?.(row, e))}
                                disabled={isLoadingPreview}
                              >
                                <FontAwesomeIcon icon={faFilePdf} />{' '}
                                {isLoadingPreview ? 'Loading...' : 'Preview PDF'}
                              </button>
                              {row.status === 'APPROVED' && (
                                <button type="button" onClick={runAction((e) => actions.onDownloadPdf?.(row, e))}>
                                  <FontAwesomeIcon icon={faDownload} /> Download PDF
                                </button>
                              )}
                              <button type="button" onClick={runAction((e) => actions.onViewAuditLog?.(row, e))}>
                                <FontAwesomeIcon icon={faClipboardList} /> View Audit Log
                              </button>
                              {VOIDABLE_STATUSES.includes(row.status) && (
                                <>
                                  <hr />
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={runAction((e) => actions.onVoid?.(row, e))}
                                  >
                                    <FontAwesomeIcon icon={faBan} /> Void Invoice
                                  </button>
                                </>
                              )}
                              {row.status !== 'APPROVED' && !['VOIDED', 'CANCELLED'].includes(row.status) && (
                                <>
                                  <hr />
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={runAction((e) => actions.onDelete?.(row, e))}
                                  >
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="payment-reminders-footer">
          <span className="payment-reminders-count">
            {confirmArmed
              ? 'Emails go out immediately and cannot be recalled.'
              : `${rows.length} invoice${rows.length === 1 ? '' : 's'} · ${selectedIds.length} ticked`}
          </span>
          <div className="payment-reminders-footer-buttons">
            <button
              type="button"
              className="payment-reminders-btn-secondary"
              onClick={() => (confirmArmed ? setConfirmArmed(false) : onClose())}
              disabled={isSending}
            >
              {confirmArmed ? 'Back' : 'Close'}
            </button>
            <button
              type="button"
              className={`payment-reminders-btn-primary${confirmArmed ? ' is-armed' : ''}`}
              onClick={handleSend}
              disabled={isSending || selectedIds.length === 0}
            >
              <FontAwesomeIcon icon={faPaperPlane} />{' '}
              {isSending
                ? 'Sending…'
                : confirmArmed
                ? `Confirm — Send ${selectedIds.length} Now`
                : `Send ${selectedIds.length} Reminder${selectedIds.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRemindersModal;
